import { ImportSubmissionModel } from 'Data/ViewModels/ImportSubmissionModel';
import { hasNonEmptyValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as importedWorksheetActions from '../../../../../Data/ReduxViewModels/ImportedWorksheet/actions';
import { actions as worksheetActions } from '../../../../../Data/ReduxViewModels/WorksheetEditor';
import * as objectPath from '../../../../../Lib/Object/Path/Immutable';
import { Button, ButtonGroup, Checkbox, Field, IMessageProps, Spinner } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from '../../../../Layout';
import { IWorksheetComponent } from '../../types';
import { AssociatedTestType, AssociatedTestTypes } from '../types';
import ImportedTestDetailsSection from './ImportedTestDetailsSection';
import { WorksheetTestSearch } from './WorksheetTestSearch';

// Needs to be a static variable as we need to increment it for each Imported Test Section on a worksheet
let idCounter = 0;
let defaultImportPropertyName = 'CommonTestLoader';

interface IWorksheetImportedTestSectionWithTestSelectionProps extends IWorksheetComponent {
  importedTestResults?: React.ReactNode;
  importPropertyName?: string;
  queryParams?: any;
  selectionFields?: React.ReactNode;
  name: string;
  importModel?: ImportSubmissionModel;
  value?: any;
  includeDescendants?: boolean;
  includeOnlyCompleted?: boolean;
}

interface IWorksheetImportedTestSectionWithSampleSelectionProps extends IWorksheetComponent {
  importedTestResults?: React.ReactNode;
  onlyThisSample?: boolean;
  name: string;
  importModel?: ImportSubmissionModel;
  testMethodIds?: string[];
  /**
   * The imported test model (including the results model for the referenced worksheet)
   * Not properly typed yet.
   *  ImportedWorksheetModel<TResultsModel>
   *  {
   *    Id: guid,
   *    Type: { worksheet type, test method, etc },
   *    Sample: { sampleId, date sampled, etc },
   *    Results: { date tested, etc - model depends on referenced worksheet }
   *    Mismatch: { true|false }
   *  }
   **/
  value?: any;
  messages?: IMessageProps[];
  testSearchLabel?: string;
  /**
   * The message that is displayed beneath the dropdown when it is empty.
   * For now it only applicable to Worksheet Test Search.
   **/
  emptyListMessage?: string;
  includeDescendants?: boolean;
}

type IWorksheetImportedTestSectionProps = IWorksheetImportedTestSectionWithSampleSelectionProps &
  IWorksheetImportedTestSectionWithTestSelectionProps;

let WorksheetImportedTestSection: React.FC<IWorksheetImportedTestSectionProps> = ({
  importedTestResults,
  onlyThisSample = false,
  prefixName = (name: string) => name, // default should never be used, prop injector will do it
  name,
  importModel,
  testMethodIds,
  createWorksheetSelector,
  value: importedTest,
  importPropertyName = defaultImportPropertyName,
  queryParams,
  selectionFields,
  messages,
  testSearchLabel,
  emptyListMessage,
  includeDescendants = false,
  includeOnlyCompleted,
}) => {
  const [editing, setEditing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const [selectedTestSectionId] = useState(`import-from-test-${idCounter++}`);
  const [includeOnlyCompletedWorksheets, setIncludeOnlyCompletedWorksheets] = useState(includeOnlyCompleted ?? false);

  const dispatch = useDispatch();

  const hasImportedTest = hasNonEmptyValue(importedTest);
  const sampleId = useSelector(createWorksheetSelector!('header.Sample.Id', false)) as string;
  const worksheetId = useSelector(createWorksheetSelector!('data.Id', false)) as Guid;
  const selectedTestId = useSelector((s) =>
    objectPath.get(s.importedWorksheet, `${selectedTestSectionId}.selectedTest.Id`)
  ) as Guid | undefined;
  const disabled = useSelector(
    (s) => objectPath.get(s.importedWorksheet, `${selectedTestSectionId}.selectedTest`) === null
  ) as boolean;
  const importing = useSelector((s) => s.worksheetEditor.status.importing);
  const synchronising = useSelector((s) => s.worksheetEditor.status.synchronising);

  useEffect(() => {
    // returned function will be called on component unmount
    return () => {
      dispatch(importedWorksheetActions.cleanupImportedWorksheet(selectedTestSectionId));
    };
  }, [dispatch, selectedTestSectionId]);

  /*
     Update the referenced worksheet on the main worksheet, and
     refresh the main worksheet with any imported test results.
   */
  const persistImportedTestId = () => {
    // If the user attempted to import the currently imported test, then do nothing
    if (selectedTestId === importModel?.Id) {
      setEditing(false);
      return;
    }
    setIsImporting(true);
    // Always set the associated test type to imported when we set the imported id, because the server will keep
    // clearing it until a valid import id is set.
    const type = AssociatedTestType.Imported;
    const associatedTestTypePath = prefixName(name.replace(AssociatedTestTypes[type].path, '.AssociatedTestType'));
    dispatch(worksheetActions.setValue({ name: associatedTestTypePath, value: type, sync: false }));
    dispatch(worksheetActions.setValue({ name: prefixName(`${name}.Id`), value: selectedTestId, sync: true }));
    setEditing(false);
  };

  /* Re-import test results from the currently referenced worksheet */
  const reimport = () => {
    setReimporting(true);
    dispatch(worksheetActions.import({ worksheetId, importModel }));
  };

  // If we rely solely on the importing status to show a spinner on the re-import button, it will spin when importing
  // anything. Using an additional flag will ensure that only the clicked re-import button shows a spinner.
  useEffect(() => {
    if (!importing) setReimporting(false);
  }, [importing]);
  useEffect(() => {
    if (!synchronising) setIsImporting(false);
  }, [synchronising]);

  const messageSection = <ListFieldSection visible={hasNonEmptyValue(messages)} messages={messages}></ListFieldSection>;

  return (
    <>
      {isImporting ? (
        <div style={{ position: 'relative', height: `20px`, width: '20px' }}>
          <Spinner />
        </div>
      ) : (
        !editing &&
        hasImportedTest && (
          <>
            {hasImportedTest && <ImportedTestDetailsSection importedTest={importedTest} />}
            {importedTestResults}
            {messageSection}
            <ButtonGroup>
              <Button onClick={reimport} loading={reimporting} visible={importedTest?.Mismatch}>
                Re-Import
              </Button>
              <Button disabled={reimporting} onClick={() => setEditing(true)} visible={!onlyThisSample}>
                Change
              </Button>
            </ButtonGroup>
          </>
        )
      )}
      {/* The reason the following div uses `display none` instead of hiding the div completely is so that we keep the loaded
      import sources after clicking change and importing from an alternate source */}
      <div style={!editing && hasImportedTest ? { display: 'none  ' } : undefined}>
        <ListFieldSection>
          <FieldSet>
            <Field
              label="Show only samples with completed tests"
              input={<Checkbox />}
              onChange={(e: any) => setIncludeOnlyCompletedWorksheets(e.data.value)}
              value={includeOnlyCompletedWorksheets}
              visible={importPropertyName === defaultImportPropertyName}
            />
            <WorksheetTestSearch
              label={testSearchLabel}
              emptyListMessage={emptyListMessage}
              selectedTestSectionId={selectedTestSectionId}
              importPropertyName={importPropertyName}
              queryParams={{
                ...queryParams,
                testMethodIds: testMethodIds?.join(','),
                sampleid: onlyThisSample ? sampleId : null,
                includeDescendantWorksheets: includeDescendants,
                includeOnlyCompletedWorksheets,
              }}
              selectionFields={selectionFields}
            />
            <ButtonGroup>
              <Button onClick={persistImportedTestId} disabled={disabled}>
                Import
              </Button>
              <Button visible={hasImportedTest} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </ButtonGroup>
          </FieldSet>
        </ListFieldSection>
        {messageSection}
      </div>
    </>
  );
};

WorksheetImportedTestSection = withInjectedProps(WorksheetImportedTestSection);

export default WorksheetImportedTestSection;
export type { IWorksheetImportedTestSectionProps };
